/**
 * LevelRegistry.ts
 * Central index of all bundled level JSONs, keyed by [worldId][levelId].
 *
 * Levels are loaded eagerly via static ES imports at module init
 * (synchronous — `tsconfig.json` enables `resolveJsonModule` and Vite
 * inlines the parsed JSON at bundle time). Each entry stores the raw
 * TileMapResult so callers can access levelData, levelRuntime,
 * checkpoints, enemySpawns, and tiledMap without re-parsing.
 *
 * DECISION: Eager imports at module scope — Vite inlines all JSON at
 * build time, so the cost is paid once during app init, not per scene
 * transition. This guarantees every level reference is validated at
 * bundle time (missing file → build-gate failure).
 *
 * @module LevelRegistry
 */

import type { WorldId } from '../../constants/gameConstants';
import { loadTileMap } from './TileMapLoader';
import type { TileMapResult } from './TileMapLoader';

// ── Level JSON imports ──────────────────────────────────────────────────────
// Each import resolves to the parsed JSON object (Vite + tsconfig
// `resolveJsonModule` handle JSON natively).

import RAW_1_1 from '../../assets/levels/world-1/level-1-1.json';
import RAW_1_2 from '../../assets/levels/world-1/level-1-2.json';
import RAW_1_3 from '../../assets/levels/world-1/level-1-3.json';
import RAW_1_4 from '../../assets/levels/world-1/level-1-4.json';
import RAW_1_S from '../../assets/levels/world-1/level-1-s.json';
// DECISION: 3-1 is a greybox seed level that reuses the World 1 Sunmeadow
// tileset until the Brasscloud Foundry art lands (ROADMAP Phase 4 — World 3
// art). Registering it here unblocks every subsequent on-foot World 3 PR
// (session helpers, /game route generalisation, world-map wiring).
import RAW_3_1 from '../../assets/levels/world-3/level-3-1.json';
import RAW_3_2 from '../../assets/levels/world-3/level-3-2.json';
import RAW_3_3 from '../../assets/levels/world-3/level-3-3.json';
import RAW_3_4 from '../../assets/levels/world-3/level-3-4.json';
// DECISION: 3-S is the World 3 secret bonus stage (canonical id 3-0,
// levelId 0 = secret per the numbering convention below). Unlocked on
// the hub world map once all 12 main-level shards are collected.
import RAW_3_S from '../../assets/levels/world-3/level-3-S.json';
// DECISION: 3-B is the World 3 boss arena (King Rivet). The session
// helper maps the user-facing id '3-B' → integer 5 so it slots into
// the existing integer-keyed Registry without inventing a parallel
// string-keyed lookup table.
import RAW_3_B from '../../assets/levels/world-3/level-3-B.json';
// DECISION: 4-1 is a greybox seed level that reuses the World 1 Sunmeadow
// tileset until the Crystal Tide Ruins art lands (ROADMAP Phase 4 — World 4
// art). Registering it here unblocks every subsequent on-foot World 4 PR
// (additional stages, boss arena, world-map wiring).
import RAW_4_1 from '../../assets/levels/world-4/level-4-1.json';
import RAW_4_2 from '../../assets/levels/world-4/level-4-2.json';
import RAW_4_3 from '../../assets/levels/world-4/level-4-3.json';
import RAW_4_4 from '../../assets/levels/world-4/level-4-4.json';
// DECISION: 4-S (id 4-0) is the World 4 exploration secret stage,
// unlocked at 12/12 World 4 shards. Registered with the secret flag like
// 1-S and 3-S; no normal progression leads out of it.
import RAW_4_S from '../../assets/levels/world-4/level-4-S.json';
// DECISION: 4-B is the World 4 boss arena (PrismLeviathan). The session
// helper maps the user-facing id '4-B' → integer 5 so it slots into
// the existing integer-keyed Registry without inventing a parallel
// string-keyed lookup table (same shape as 3-B).
import RAW_4_B from '../../assets/levels/world-4/level-4-B.json';
// DECISION: 5-1 is a greybox seed level that reuses the World 1 Sunmeadow
// tileset until the Eclipse Bastion art lands (ROADMAP Phase 4 — World 5
// art). Registering it here unblocks every subsequent World 5 PR
// (additional stages, StarEater boss arena, secret stage, world-map
// wiring). The runtime PNG swap to world-5-eclipsebastion.png happens in
// setActiveWorldId(5).
import RAW_5_1 from '../../assets/levels/world-5/level-5-1.json';
// DECISION: 5-2..5-4 are the World 5 main-progression greybox stages
// (escalation → mid-world → boss-approach), reusing the wired W3/W4 ground
// roster until the W5-native enemies land in the 5-B PR. Each carries 3
// Constellation Shards so 5-1..5-4 hold the 12-shard world invariant.
import RAW_5_2 from '../../assets/levels/world-5/level-5-2.json';
import RAW_5_3 from '../../assets/levels/world-5/level-5-3.json';
import RAW_5_4 from '../../assets/levels/world-5/level-5-4.json';
// DECISION: 5-S (id 5-0) is the World 5 exploration secret stage, unlocked
// at 12/12 World 5 shards. Registered with the secret flag like 1-S / 3-S /
// 4-S; its shards live under key 5-0 and do not count toward the gate.
import RAW_5_S from '../../assets/levels/world-5/level-5-S.json';
// DECISION: 5-B is the World 5 boss arena (Star-Eater) and the final
// stage of the game. The session helper maps the user-facing id '5-B' →
// integer 5, matching the 3-B / 4-B convention so it slots into the same
// levelKey space without colliding with the 5-1..5-4 main stages or the
// 5-0 secret.
import RAW_5_B from '../../assets/levels/world-5/level-5-B.json';

// ── Parsed registry ──────────────────────────────────────────────────────────
// DECISION: Level numbering — normal levels are 1-based (1–4), secret stage
// uses levelId 0 to keep the index space clean and easily distinguishable.

/** Level key: `"${worldId}-${levelId}"` where levelId 0 = secret stage. */
export type LevelKey = `${WorldId}-${number}`;

/**
 * Build a LevelKey string from world and level identifiers.
 *
 * @param worldId - World number (1–5).
 * @param levelId - Level number within the world (1–4 normal, 0 secret).
 * @returns A canonical level key string.
 */
export function levelKey(worldId: WorldId, levelId: number): LevelKey {
  return `${worldId}-${levelId}`;
}

// DECISION: Parse all levels at module load. Parsing is pure CPU (no I/O),
// takes < 10ms per level, and avoids lazy-init timing surprises in gameplay.
const REGISTRY: ReadonlyMap<LevelKey, TileMapResult> = new Map<LevelKey, TileMapResult>([
  [levelKey(1, 1), loadTileMap(RAW_1_1, 1, 1)],
  [levelKey(1, 2), loadTileMap(RAW_1_2, 1, 2)],
  [levelKey(1, 3), loadTileMap(RAW_1_3, 1, 3)],
  [levelKey(1, 4), loadTileMap(RAW_1_4, 1, 4)],
  [levelKey(1, 0), loadTileMap(RAW_1_S, 1, 0, true)],
  [levelKey(3, 1), loadTileMap(RAW_3_1, 3, 1)],
  [levelKey(3, 2), loadTileMap(RAW_3_2, 3, 2)],
  [levelKey(3, 3), loadTileMap(RAW_3_3, 3, 3)],
  [levelKey(3, 4), loadTileMap(RAW_3_4, 3, 4)],
  [levelKey(3, 0), loadTileMap(RAW_3_S, 3, 0, true)],
  [levelKey(3, 5), loadTileMap(RAW_3_B, 3, 5)],
  [levelKey(4, 1), loadTileMap(RAW_4_1, 4, 1)],
  [levelKey(4, 2), loadTileMap(RAW_4_2, 4, 2)],
  [levelKey(4, 3), loadTileMap(RAW_4_3, 4, 3)],
  [levelKey(4, 4), loadTileMap(RAW_4_4, 4, 4)],
  [levelKey(4, 0), loadTileMap(RAW_4_S, 4, 0, true)],
  [levelKey(4, 5), loadTileMap(RAW_4_B, 4, 5)],
  [levelKey(5, 1), loadTileMap(RAW_5_1, 5, 1)],
  [levelKey(5, 2), loadTileMap(RAW_5_2, 5, 2)],
  [levelKey(5, 3), loadTileMap(RAW_5_3, 5, 3)],
  [levelKey(5, 4), loadTileMap(RAW_5_4, 5, 4)],
  [levelKey(5, 0), loadTileMap(RAW_5_S, 5, 0, true)],
  [levelKey(5, 5), loadTileMap(RAW_5_B, 5, 5)],
]);

/**
 * Retrieve parsed level data by world and level number.
 *
 * @param worldId - World number (1–5).
 * @param levelId - Level number (1–4 normal, 0 for secret stage).
 * @returns The parsed TileMapResult, or undefined if the level is not registered.
 */
export function getLevel(worldId: WorldId, levelId: number): TileMapResult | undefined {
  return REGISTRY.get(levelKey(worldId, levelId));
}

/**
 * Get all level keys registered for a given world.
 *
 * @param worldId - World number to query.
 * @returns Array of LevelKey strings for that world, sorted ascending.
 */
export function getWorldLevels(worldId: WorldId): LevelKey[] {
  const prefix = `${worldId}-`;
  return [...REGISTRY.keys()]
    .filter(k => k.startsWith(prefix))
    // DECISION: Numeric sort by levelId to keep correct progression order.
    // String sort would put "1-10" before "1-2".
    .sort((a, b) => {
      const aId = Number(a.split('-')[1]);
      const bId = Number(b.split('-')[1]);
      return aId - bId;
    });
}

/**
 * Total number of registered levels across all worlds.
 *
 * @returns The count of entries in the registry.
 */
export function levelCount(): number {
  return REGISTRY.size;
}
